import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { Modal } from "../components/Modal";
import { AdminActionButton, DeleteIcon, EditIcon, IconActionButton } from "../components/AdminActions";
import { confirmDelete, deleteFailed } from "../utils/confirmDelete";
import type { Organizer, OrganizerUser } from "../types";

type Editor =
  | { kind: "organizer"; item?: Organizer }
  | { kind: "user"; organizer: Organizer; item?: OrganizerUser }
  | null;

export function OrganizersPage() {
  const [items, setItems] = useState<Organizer[]>([]);
  const [editor, setEditor] = useState<Editor>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    const result = await api<{ items: Organizer[] }>("/api/organizers");
    setItems(result.items);
  }, []);
  useEffect(() => { void load().catch((error) => setMessage(error.message)); }, [load]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editor) return;
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setMessage("");
    try {
      if (editor.kind === "organizer") {
        const item = editor.item;
        await api(`/api/organizers${item ? `/${item.id}` : ""}`, {
          method: item ? "PUT" : "POST",
          body: JSON.stringify(item ? {
            name: form.get("name"), active: form.has("active"),
          } : {
            name: form.get("name"), adminName: form.get("adminName"), adminEmail: form.get("adminEmail"),
            adminPhone: form.get("adminPhone"), password: form.get("password"), active: form.has("active"),
          }),
        });
      } else {
        const { organizer, item } = editor;
        await api(`/api/organizers/${organizer.id}/users${item ? `/${item.id}` : ""}`, {
          method: item ? "PUT" : "POST",
          body: JSON.stringify({ name: form.get("name"), email: form.get("email"), phone: form.get("phone"),
            password: form.get("password"), active: form.has("active") }),
        });
      }
      setEditor(null);
      setMessage("Cambios guardados correctamente");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "No se pudieron guardar los cambios");
    } finally { setSaving(false); }
  }

  async function removeUser(organizer: Organizer, item: OrganizerUser) {
    if (!await confirmDelete(item.name, `Se eliminará su acceso a todos los eventos de ${organizer.name}.`)) return;
    try {
      await api(`/api/organizers/${organizer.id}/users/${item.id}`, { method: "DELETE" });
      setMessage("Usuario eliminado");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "No se pudo eliminar el usuario");
      await deleteFailed(error);
    }
  }

  return <div className="page-stack">
    <header className="page-header"><div><span className="eyebrow">Administración general</span><h1>Organizadores</h1><p>Entidades organizadoras, sus administradores y los eventos que pueden gestionar.</p></div><IconActionButton label="Nuevo organizador" icon="add" tone="primary" onClick={() => setEditor({ kind: "organizer" })} /></header>
    {message && <div className="inline-notice">{message}</div>}
    <div className="event-card-grid organizer-card-grid">{items.map((item) => <article className="event-admin-card organizer-admin-card" key={item.id}>
      <div className="event-card-top"><span className="event-monogram">{item.name.slice(0, 3).toUpperCase()}</span><span className={`status-badge status-${item.active ? "active" : "inactive"}`}>{item.active ? "Activo" : "Inactivo"}</span></div>
      <h2>{item.name}</h2>
      <div className="event-card-metrics"><div><strong>{item.eventCount}</strong><span>eventos</span></div><div><strong>{item.userCount}</strong><span>administradores</span></div></div>
      <div className="organizer-users">
        <div className="organizer-users-heading"><strong>Usuarios del organizador</strong><IconActionButton label={`Agregar usuario a ${item.name}`} icon="add" tone="secondary" onClick={() => setEditor({ kind: "user", organizer: item })} disabled={!item.active} /></div>
        {item.users.length ? item.users.map((account) => <div className="organizer-user-row" key={account.id}>
          <div><strong>{account.name}</strong><span>{account.email}</span><small>{account.phone}</small></div>
          <span className={`status-badge status-${account.active ? "active" : "inactive"}`}>{account.active ? "Activo" : "Inactivo"}</span>
          <div className="admin-icon-actions"><AdminActionButton tone="edit" label={`Editar a ${account.name}`} onClick={() => setEditor({ kind: "user", organizer: item, item: account })}><EditIcon /></AdminActionButton><AdminActionButton tone="delete" label={`Eliminar a ${account.name}`} onClick={() => void removeUser(item, account)}><DeleteIcon /></AdminActionButton></div>
        </div>) : <p className="organizer-users-empty">Sin usuarios administradores asignados.</p>}
      </div>
      <div className="event-card-actions"><span className="system-role">Acceso a todos sus eventos</span><AdminActionButton tone="edit" label={`Editar ${item.name}`} onClick={() => setEditor({ kind: "organizer", item })}><EditIcon /></AdminActionButton></div>
    </article>)}</div>
    {editor && <Modal title={editor.kind === "organizer" ? `${editor.item ? "Editar" : "Nuevo"} organizador` : `${editor.item ? "Editar" : "Nuevo"} usuario`} eyebrow="Organizadores" wide onClose={() => setEditor(null)}><form onSubmit={save}>{editor.kind === "organizer" ? <OrganizerForm item={editor.item} /> : <OrganizerUserForm organizer={editor.organizer} item={editor.item} />}<div className="modal-footer"><IconActionButton label="Cancelar" icon="cancel" onClick={() => setEditor(null)} /><IconActionButton label="Guardar" icon="save" tone="primary" type="submit" busy={saving} disabled={saving} /></div></form></Modal>}
  </div>;
}

function OrganizerForm({ item }: { item?: Organizer }) {
  return <div className="form-grid">
    <label className="field field-wide"><span>Entidad organizadora *</span><input name="name" required defaultValue={item?.name} placeholder="Ej. Sociedad Argentina de Cardiología" /></label>
    {!item && <>
      <label className="field"><span>Primer administrador *</span><input name="adminName" required /></label>
      <label className="field"><span>Email *</span><input name="adminEmail" type="email" required /></label>
      <label className="field"><span>Celular con WhatsApp *</span><input name="adminPhone" type="tel" required placeholder="+54 9 11…" /></label>
      <label className="field"><span>Contraseña *</span><input name="password" type="password" minLength={6} required autoComplete="new-password" /></label>
    </>}
    <label className="check-field field-wide"><input name="active" type="checkbox" defaultChecked={item ? Boolean(item.active) : true} /><span>Organizador activo</span></label>
    {item && <p className="field-help field-wide">Los usuarios se administran desde la tarjeta del organizador.</p>}
  </div>;
}

function OrganizerUserForm({ organizer, item }: { organizer: Organizer; item?: OrganizerUser }) {
  return <div className="form-grid">
    <div className="inline-notice field-wide">Este usuario podrá administrar todos los eventos de <strong>{organizer.name}</strong> y crear nuevos eventos para esta entidad.</div>
    <label className="field"><span>Nombre *</span><input name="name" required defaultValue={item?.name} /></label>
    <label className="field"><span>Email *</span><input name="email" type="email" required defaultValue={item?.email} /></label>
    <label className="field"><span>Celular con WhatsApp *</span><input name="phone" type="tel" required defaultValue={item?.phone} placeholder="+54 9 11…" /></label>
    <label className="field"><span>{item ? "Nueva contraseña (opcional)" : "Contraseña *"}</span><input name="password" type="password" minLength={6} required={!item} autoComplete="new-password" /></label>
    <label className="check-field field-wide"><input name="active" type="checkbox" defaultChecked={item ? Boolean(item.active) : true} /><span>Usuario activo</span></label>
  </div>;
}
